/** Задать «зарплату себе» — то самое число, вокруг которого весь продукт.
 *
 * Валюта своя, а не базовая. Владелец думает о лимите в рублях, а итоги считает
 * в долларах; заставить его конвертировать в уме — верный способ получить лимит
 * 150 000 $ вместо 1917 $ (FR-69b). Пересчёт делает сервер по курсу дня.
 */
import { useState } from "react";
import { ApiError, api, type Allowance } from "../lib/api.ts";
import { dayLabel } from "../lib/group.ts";
import { formatMoney } from "../lib/money.ts";
import { Sheet } from "./Sheet.tsx";
import { tap } from "../lib/telegram.ts";
import { hint } from "./ui.tsx";
import { onRadioKeys } from "../lib/radiogroup.ts";

const CURRENCIES = [["USD", "$"], ["VND", "₫"], ["RUB", "₽"]] as const;

export function LimitSheet({ state, onClose, onDone }: {
  state: Allowance | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const submit = () => {
    tap();
    setBusy(true);
    setTrouble("");
    api.setAllowance(amount.trim(), currency)
      .then(onDone)
      .catch((e) => setTrouble(e instanceof ApiError && e.kind === "unclear"
        ? "Не разобрал сумму. Только цифры."
        : "Не получилось сохранить. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  return (
    <Sheet title={state ? "Изменить лимит" : "Задать лимит"} onClose={onClose}>
      <p style={hint}>
        Сколько готов тратить в месяц. На каждой трате будет видно, сколько ещё можно —
        это не запрет, а ответ на вопрос.
      </p>

      {state && (
        <div style={{ background: "var(--surface)", borderRadius: "var(--r-card)",
                      padding: "12px 14px", margin: "var(--s-4) 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "baseline" }}>
            <span style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
              Сейчас
            </span>
            <span className="num" style={{ fontSize: "var(--f-body)", fontWeight: 600 }}>
              {formatMoney(state.limit, state.base_currency)}
            </span>
          </div>
          {/* Дата начала объясняет, почему остаток бывает меньше ожидаемого:
              лимит, заданный в середине месяца, действует пропорционально. */}
          {state.since && (
            <p style={{ ...hint, marginTop: 6 }}>
              Действует с {dayLabel(state.since)} — за неполный месяц считается
              пропорционально.
            </p>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "var(--s-2)", marginTop: "var(--s-4)" }}>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
               placeholder="1800" aria-label="Сумма лимита" autoFocus
               style={{ flexGrow: 1, minWidth: 0, minHeight: "var(--tap)",
                        background: "var(--surface-2)", border: "none",
                        borderRadius: "var(--r-field)", padding: "12px 14px",
                        color: "var(--text)", font: "inherit", fontSize: "var(--f-t3)",
                        fontWeight: 600 }} />
        <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Валюта лимита"
             style={{ display: "flex", gap: 4, background: "var(--surface-2)",
                      borderRadius: "var(--r-field)", padding: 4 }}>
          {CURRENCIES.map(([code, sign]) => (
            <button key={code} type="button" role="radio" aria-checked={currency === code}
                    aria-label={code} onClick={() => setCurrency(code)}
                    style={{ minWidth: 40, minHeight: 40, border: "none", cursor: "pointer",
                             borderRadius: "var(--r-sm)", font: "inherit",
                             fontSize: "var(--f-body)",
                             fontWeight: currency === code ? 700 : 400,
                             background: currency === code ? "var(--accent)" : "transparent",
                             color: currency === code ? "var(--on-accent)" : "var(--text)" }}>
              {sign}
            </button>
          ))}
        </div>
      </div>

      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}

      <button type="button" disabled={!amount.trim() || busy} onClick={submit}
              style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-4)",
                       padding: 14, borderRadius: "var(--r-field)", border: "none",
                       cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                       fontWeight: 600,
                       background: amount.trim() && !busy ? "var(--accent)" : "var(--surface-2)",
                       color: amount.trim() && !busy ? "var(--on-accent)" : "var(--text-disabled)" }}>
        Сохранить
      </button>

      <p style={hint}>
        Можно в любой валюте — пересчитается по сегодняшнему курсу в
        {" "}{state?.base_currency ?? "базовую валюту"}.
      </p>
    </Sheet>
  );
}

